// ============================================================================
//  VOICE CHAT SERVICE

// ============================================================================
// Handles Agora voice chat, promo code validation, and payment processing
// - $0.50 PKCash to enable voice chat on table creation
// - Bypass codes waive the fee (managed by bypassCodeService)
// ============================================================================

import { db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  addDoc,
  serverTimestamp,
  increment,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import walletService from './walletService';
import bypassCodeService from './bypassCodeService';

// Initialize Firebase Functions for secure backend calls
const functions = getFunctions();
const processVoiceChatPaymentAPI = httpsCallable(functions, 'processVoiceChatPayment');

// ============================================================================
// CONSTANTS
// ============================================================================

export const VOICE_CHAT_FEE = 0.50; // PKCash cost to enable voice chat (legal states)
export const VOICE_CHAT_SESSION_FEE = 1.99; // PKCash cost for 24hr session (restricted states)
export const VOICE_CHAT_SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours in ms
// Same fallback as useAgoraMedia - one Agora project everywhere, matching
// the server-side token signer in functions/.env.
export const AGORA_APP_ID = process.env.REACT_APP_AGORA_APP_ID || '1a46421df9b74205b5fc031939545903';

// ============================================================================
// TYPES
// ============================================================================

export interface VoiceChatValidation {
  isValid: boolean;
  isFreePromo: boolean;
  agentId?: string;
  agentName?: string;
  codeId?: string;
  ownerType?: 'agent' | 'admin';
  error?: string;
}

export interface VoiceChatPaymentResult {
  success: boolean;
  transactionId?: string;
  error?: string;
}

export interface VoiceChatSession {
  tableId: string;
  channelName: string;
  hostId: string;
  createdAt: Date;
  participants: string[];
  isActive: boolean;
  promoCodeUsed?: string;
  agentId?: string;
  feePaid: number;
}

export interface VoiceChatStats {
  totalSessions: number;
  totalRevenue: number;
  freeSessionsViaPromo: number;
  paidSessions: number;
}

export interface VoiceChatTransaction {
  id: string;
  userId: string;
  tableId: string;
  tableName?: string;
  amount: number;
  type: 'voice_chat_fee' | 'voice_chat_session' | 'promo_waived' | 'promo_waived_legacy' | 'refund';
  promoCode?: string;
  codeId?: string;
  agentId?: string;
  createdAt: Date;
  //  NEW: Session tracking

  sessionId?: string;
  expiresAt?: Date;
  isSessionBased?: boolean;
}

//  NEW: Session-based voice chat for restricted states

export interface VoiceChatUserSession {
  id: string;
  odId: string;
  purchasedAt: Date;
  expiresAt: Date;
  amount: number;
  tablesUsed: string[];
  isActive: boolean;
}

// ============================================================================
// PROMO CODE VALIDATION
// ============================================================================

/**
 * Validate a promo code against bypass codes
 * Uses the new bypassCodeService for proper code management
 */
export const validatePromoCode = async (promoCode: string): Promise<VoiceChatValidation> => {
  if (!promoCode || promoCode.trim().length < 3) {
    return { isValid: false, isFreePromo: false, error: 'Invalid promo code' };
  }

  const code = promoCode.trim().toUpperCase();

  try {
    // Validate against bypassCodeService
    const result = await bypassCodeService.validateCode(code);

    if (result.valid) {
      console.log(`✅ Valid bypass code: ${code} (owner: ${result.ownerName})`);
      return {
        isValid: true,
        isFreePromo: true,
        agentId: result.ownerId,
        agentName: result.ownerName,
        codeId: result.codeId,
        ownerType: result.ownerType,
      };
    }

    // Code not found or inactive
    return { 
      isValid: false, 
      isFreePromo: false, 
      error: result.error || 'Promo code not found' 
    };

  } catch (error) {
    console.error('Error validating promo code:', error);
    return { isValid: false, isFreePromo: false, error: 'Failed to validate code' };
  }
};

/**
 * Legacy validation that also checks agent referral codes
 * This provides backwards compatibility if needed
 */
export const validatePromoCodeLegacy = async (promoCode: string): Promise<VoiceChatValidation> => {
  if (!promoCode || promoCode.trim().length < 3) {
    return { isValid: false, isFreePromo: false, error: 'Invalid promo code' };
  }

  const code = promoCode.trim().toUpperCase();

  try {
    // First try the new bypass code system
    const bypassResult = await bypassCodeService.validateCode(code);

    if (bypassResult.valid) {
      return {
        isValid: true,
        isFreePromo: true,
        agentId: bypassResult.ownerId,
        agentName: bypassResult.ownerName,
        codeId: bypassResult.codeId,
        ownerType: bypassResult.ownerType,
      };
    }

    // Fallback: Check agents collection for referralCode (legacy support)
    const agentsRef = collection(db, 'agents');
    
    // Check referralCode
    const refCodeQuery = query(
      agentsRef, 
      where('referralCode', '==', code),
      where('active', '==', true)
    );
    const refCodeSnapshot = await getDocs(refCodeQuery);
    
    if (!refCodeSnapshot.empty) {
      const agentDoc = refCodeSnapshot.docs[0];
      const agentData = agentDoc.data();
      console.log(`⚠️ Legacy referral code used: ${code} - Consider migrating to bypass codes`);
      return {
        isValid: true,
        isFreePromo: true,
        agentId: agentDoc.id,
        agentName: agentData.username || agentData.name || 'Agent',
      };
    }

    // Check customCode (legacy)
    const customCodeQuery = query(
      agentsRef,
      where('customCode', '==', code),
      where('active', '==', true)
    );
    const customCodeSnapshot = await getDocs(customCodeQuery);
    
    if (!customCodeSnapshot.empty) {
      const agentDoc = customCodeSnapshot.docs[0];
      const agentData = agentDoc.data();
      console.log(`⚠️ Legacy custom code used: ${code} - Consider migrating to bypass codes`);
      return {
        isValid: true,
        isFreePromo: true,
        agentId: agentDoc.id,
        agentName: agentData.username || agentData.name || 'Agent',
      };
    }

    // No matching code found
    return { isValid: false, isFreePromo: false, error: 'Promo code not found' };

  } catch (error) {
    console.error('Error validating promo code:', error);
    return { isValid: false, isFreePromo: false, error: 'Failed to validate code' };
  }
};

// ============================================================================
// PAYMENT PROCESSING
// ============================================================================

/**
 * Process payment for voice chat feature
 *  SECURE: Now calls Cloud Function for server-side validation

 */
export const processVoiceChatPayment = async (
  userId: string,
  tableId: string,
  tableName?: string,
  promoCode?: string,
  codeId?: string,
  agentId?: string,
  userName?: string
): Promise<VoiceChatPaymentResult> => {
  try {
    console.log(`🎙️ [VOICE_CHAT] Processing payment for ${userId}, table: ${tableId}`);
    
    // Call secure backend Cloud Function
    const result = await processVoiceChatPaymentAPI({
      tableId,
      tableName,
      promoCode,
      codeId,
      agentId,
    });
    
    const data = result.data as { success: boolean; transactionId?: string; error?: string; waived?: boolean };
    
    if (data.success) {
      if (data.waived) {
        console.log(`🎟️ [VOICE_CHAT] Fee waived via promo code`);
      } else {
        console.log(`💰 [VOICE_CHAT] Payment processed: ${data.transactionId}`);
      }
      return { success: true, transactionId: data.transactionId };
    } else {
      return { success: false, error: data.error || 'Payment failed' };
    }
  } catch (error: any) {
    console.error('Error processing voice chat payment:', error);
    // Extract error message from Firebase function error
    const errorMessage = error.message || error.details || 'Payment processing failed';
    return { success: false, error: errorMessage };
  }
};

/**
 * Refund voice chat payment (if table creation fails)
 */
export const refundVoiceChatPayment = async (
  userId: string,
  tableId: string,
  originalTransactionId: string
): Promise<boolean> => {
  try {
    // Add PKCash back to wallet
    const walletRef = doc(db, 'wallets', userId);
    await updateDoc(walletRef, {
      pkCash: increment(VOICE_CHAT_FEE),
      updatedAt: serverTimestamp(),
    });

    // Record refund transaction
    await addDoc(collection(db, 'voiceChatTransactions'), {
      userId,
      tableId,
      amount: VOICE_CHAT_FEE,
      type: 'refund',
      originalTransactionId,
      createdAt: serverTimestamp(),
    });

    console.log(`💰 Refunded voice chat fee to user: ${userId}`);
    return true;
  } catch (error) {
    console.error('Error refunding voice chat payment:', error);
    return false;
  }
};

// ============================================================================
// AGORA TOKEN GENERATION (Client-side for development)
// ============================================================================
// NOTE: For production, tokens should be generated server-side
// This is a simplified version using null token (works for testing)

/**
 * Get Agora connection config for a table
 * In production, you'd call a backend endpoint to get a secure token
 */
export const getAgoraConfig = (tableId: string, userId: string) => {
  return {
    appId: AGORA_APP_ID,
    channel: `table-${tableId}`, // Must match useAgoraMedia format!
    token: null, // For testing - use token server in production
    uid: null, // Agora will assign a UID
  };
};

/**
 * Generate channel name for a table
 */
export const generateChannelName = (tableId: string): string => {
  return `table-${tableId}`;
};

// ============================================================================
// TABLE VOICE CHAT STATUS
// ============================================================================

/**
 * Check if a table has voice chat enabled
 */
export const getTableVoiceChatStatus = async (tableId: string): Promise<{
  enabled: boolean;
  hostId?: string;
  participantCount?: number;
}> => {
  try {
    const tableRef = doc(db, 'tables', tableId);
    const tableDoc = await getDoc(tableRef);
    
    if (!tableDoc.exists()) {
      return { enabled: false };
    }

    const data = tableDoc.data();
    return {
      enabled: data.voiceChatEnabled === true,
      hostId: data.ownerId,
      participantCount: data.voiceChatParticipants || 0,
    };
  } catch (error) {
    console.error('Error getting voice chat status:', error);
    return { enabled: false };
  }
};

/**
 * Update voice chat participant count (for UI)
 */
export const updateVoiceChatParticipants = async (
  tableId: string,
  participantCount: number
): Promise<void> => {
  try {
    const tableRef = doc(db, 'tables', tableId);
    await updateDoc(tableRef, {
      voiceChatParticipants: participantCount,
      voiceChatUpdatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating voice chat participants:', error);
  }
};

/**
 * Enable voice chat on a table
 */
export const enableVoiceChatOnTable = async (tableId: string): Promise<boolean> => {
  try {
    const tableRef = doc(db, 'tables', tableId);
    await updateDoc(tableRef, {
      voiceChatEnabled: true,
      voiceChatParticipants: 0,
      voiceChatUpdatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error enabling voice chat on table:', error);
    return false;
  }
};

/**
 * Disable voice chat on a table
 */
export const disableVoiceChatOnTable = async (tableId: string): Promise<boolean> => {
  try {
    const tableRef = doc(db, 'tables', tableId);
    await updateDoc(tableRef, {
      voiceChatEnabled: false,
      voiceChatParticipants: 0,
      voiceChatUpdatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error disabling voice chat on table:', error);
    return false;
  }
};

// ============================================================================
// STATISTICS
// ============================================================================

/**
 * Get voice chat statistics for admin dashboard
 */
export const getVoiceChatStats = async (): Promise<VoiceChatStats> => {
  try {
    const q = query(collection(db, 'voiceChatTransactions'));
    const snapshot = await getDocs(q);

    let totalSessions = 0;
    let totalRevenue = 0;
    let freeSessionsViaPromo = 0;
    let paidSessions = 0;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      totalSessions++;

      if (data.type === 'voice_chat_fee') {
        totalRevenue += data.amount || 0;
        paidSessions++;
      } else if (data.type === 'promo_waived' || data.type === 'promo_waived_legacy') {
        freeSessionsViaPromo++;
      }
    });

    return {
      totalSessions,
      totalRevenue,
      freeSessionsViaPromo,
      paidSessions,
    };
  } catch (error) {
    console.error('Error getting voice chat stats:', error);
    return {
      totalSessions: 0,
      totalRevenue: 0,
      freeSessionsViaPromo: 0,
      paidSessions: 0,
    };
  }
};

/**
 * Get voice chat revenue for a specific time period
 */
export const getVoiceChatRevenue = async (
  startDate: Date,
  endDate: Date
): Promise<number> => {
  try {
    const q = query(
      collection(db, 'voiceChatTransactions'),
      where('type', '==', 'voice_chat_fee'),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      where('createdAt', '<=', Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(q);
    let revenue = 0;

    snapshot.forEach((docSnap) => {
      revenue += docSnap.data().amount || 0;
    });

    return revenue;
  } catch (error) {
    console.error('Error getting voice chat revenue:', error);
    return 0;
  }
};

/**
 * Get recent voice chat transactions
 */
export const getRecentVoiceChatTransactions = async (
  limitCount: number = 50
): Promise<VoiceChatTransaction[]> => {
  try {
    const q = query(
      collection(db, 'voiceChatTransactions'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const transactions: VoiceChatTransaction[] = [];

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      transactions.push({
        id: docSnap.id,
        userId: data.userId,
        tableId: data.tableId,
        tableName: data.tableName,
        amount: data.amount || 0,
        type: data.type,
        promoCode: data.promoCode,
        codeId: data.codeId,
        agentId: data.agentId,
        createdAt: data.createdAt?.toDate() || new Date(),
      });
    });

    return transactions;
  } catch (error) {
    console.error('Error getting recent transactions:', error);
    return [];
  }
};

/**
 * Get voice chat transactions for a specific user
 */
export const getUserVoiceChatTransactions = async (
  userId: string,
  limitCount: number = 20
): Promise<VoiceChatTransaction[]> => {
  try {
    const q = query(
      collection(db, 'voiceChatTransactions'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const transactions: VoiceChatTransaction[] = [];

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      transactions.push({
        id: docSnap.id,
        userId: data.userId,
        tableId: data.tableId,
        tableName: data.tableName,
        amount: data.amount || 0,
        type: data.type,
        promoCode: data.promoCode,
        codeId: data.codeId,
        agentId: data.agentId,
        createdAt: data.createdAt?.toDate() || new Date(),
      });
    });

    return transactions;
  } catch (error) {
    console.error('Error getting user transactions:', error);
    return [];
  }
};

/**
 * Get monthly voice chat stats for charts
 */
export const getMonthlyVoiceChatStats = async (
  monthsBack: number = 6
): Promise<{ month: string; revenue: number; paid: number; free: number }[]> => {
  try {
    const now = new Date();
    const stats: { month: string; revenue: number; paid: number; free: number }[] = [];

    for (let i = 0; i < monthsBack; i++) {
      const startDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);

      const q = query(
        collection(db, 'voiceChatTransactions'),
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<=', Timestamp.fromDate(endDate))
      );

      const snapshot = await getDocs(q);
      let revenue = 0;
      let paid = 0;
      let free = 0;

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.type === 'voice_chat_fee') {
          revenue += data.amount || 0;
          paid++;
        } else if (data.type === 'promo_waived' || data.type === 'promo_waived_legacy') {
          free++;
        }
      });

      const monthName = startDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      stats.unshift({ month: monthName, revenue, paid, free });
    }

    return stats;
  } catch (error) {
    console.error('Error getting monthly stats:', error);
    return [];
  }
};

// ============================================================================
//  NEW: SESSION-BASED PRICING HELPERS

// ============================================================================

/**
 * Get voice chat fee based on user's location
 * Legal states: $0.50 per table
 * Restricted states: $1.99 for 24hr session
 */
export const getVoiceChatFee = async (userId: string): Promise<{
  fee: number;
  isSessionBased: boolean;
  isRestrictedState: boolean;
  stateCode?: string;
}> => {
  try {
    // Import geoService dynamically to avoid circular deps
    const geoService = (await import('./geoService')).default;
    const geoResult = await geoService.checkLocation();
    
    const isRestricted = geoResult.isRestrictedState === true;
    
    return {
      fee: isRestricted ? VOICE_CHAT_SESSION_FEE : VOICE_CHAT_FEE,
      isSessionBased: isRestricted,
      isRestrictedState: isRestricted,
      stateCode: geoResult.region,
    };
  } catch (error) {
    console.error('Error getting voice chat fee:', error);
    // Default to standard fee on error
    return {
      fee: VOICE_CHAT_FEE,
      isSessionBased: false,
      isRestrictedState: false,
    };
  }
};

/**
 * Check if user has an active voice chat session
 */
export const getActiveVoiceSession = async (userId: string): Promise<VoiceChatUserSession | null> => {
  try {
    const sessionsRef = collection(db, 'voiceChatSessions');
    const q = query(
      sessionsRef,
      where('userId', '==', userId),
      where('isActive', '==', true),
      where('expiresAt', '>', Timestamp.now()),
      orderBy('expiresAt', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    const data = doc.data();
    
    return {
      id: doc.id,
      odId: data.userId,
      purchasedAt: data.purchasedAt?.toDate() || new Date(),
      expiresAt: data.expiresAt?.toDate() || new Date(),
      amount: data.amount || VOICE_CHAT_SESSION_FEE,
      tablesUsed: data.tablesUsed || [],
      isActive: true,
    };
  } catch (error) {
    console.error('Error checking active voice session:', error);
    return null;
  }
};

/**
 * Add a table to an existing voice session
 */
export const addTableToVoiceSession = async (sessionId: string, tableId: string): Promise<boolean> => {
  try {
    const sessionRef = doc(db, 'voiceChatSessions', sessionId);
    await updateDoc(sessionRef, {
      tablesUsed: increment(1) as any, // This won't work, need array union
    });
    
    // Actually use arrayUnion
    const { arrayUnion } = await import('firebase/firestore');
    await updateDoc(sessionRef, {
      tablesUsed: arrayUnion(tableId),
    });
    
    return true;
  } catch (error) {
    console.error('Error adding table to voice session:', error);
    return false;
  }
};

// ============================================================================
// EXPORT SERVICE
// ============================================================================

const voiceChatService = {
  // Constants
  VOICE_CHAT_FEE,
  VOICE_CHAT_SESSION_FEE,
  VOICE_CHAT_SESSION_DURATION_MS,
  AGORA_APP_ID,
  
  // Promo code validation
  validatePromoCode,
  validatePromoCodeLegacy,
  
  // Payment processing
  processVoiceChatPayment,
  refundVoiceChatPayment,
  
  //  NEW: Session-based pricing

  getVoiceChatFee,
  getActiveVoiceSession,
  addTableToVoiceSession,
  
  // Agora config
  getAgoraConfig,
  generateChannelName,
  
  // Table status
  getTableVoiceChatStatus,
  updateVoiceChatParticipants,
  enableVoiceChatOnTable,
  disableVoiceChatOnTable,
  
  // Statistics
  getVoiceChatStats,
  getVoiceChatRevenue,
  getRecentVoiceChatTransactions,
  getUserVoiceChatTransactions,
  getMonthlyVoiceChatStats,
};

export default voiceChatService;